/**
 * Quran audio provider service.
 *
 * Default CDN: everyayah.com — supports CORS, well-established PWA-friendly
 * source widely used by Quran apps.
 *
 * URL format: https://everyayah.com/data/{edition}/{SSS}{AAA}.mp3
 *   where SSS = zero-padded 3-digit surah number (001..114)
 *         AAA = zero-padded 3-digit ayah number (001..286)
 *
 * No secret keys. licenseStatus on every reciter for editorial review.
 */

const DEFAULT_PROVIDER = 'everyayah.com';
const DEFAULT_RECITER_ID = 'alafasy';

/**
 * @typedef {Object} ReciterConfig
 * @property {string} id
 * @property {string} label
 * @property {string} provider
 * @property {number} bitrate
 * @property {string} edition           - everyayah.com directory slug
 * @property {'hafs'|'warsh'|'qalun'|'unknown'} riwaya
 * @property {boolean} downloadAllowed
 * @property {'to_verify'|'verified'|'restricted'} licenseStatus
 */

/** @type {ReciterConfig[]} */
const RECITERS = [
  {
    id: 'alafasy',
    label: 'Mishary Rashid Alafasy',
    provider: DEFAULT_PROVIDER,
    bitrate: 128,
    edition: 'Alafasy_128kbps',
    riwaya: 'hafs',
    downloadAllowed: true,
    licenseStatus: 'to_verify',
  },
  {
    id: 'ghamdi',
    label: 'Saad Al-Ghamdi',
    provider: DEFAULT_PROVIDER,
    bitrate: 40,
    edition: 'Ghamadi_40kbps',
    riwaya: 'hafs',
    downloadAllowed: true,
    licenseStatus: 'to_verify',
  },
  {
    id: 'dossari',
    label: 'Yasser Al-Dosari',
    provider: DEFAULT_PROVIDER,
    bitrate: 128,
    edition: 'Yasser_Ad-Dussary_128kbps',
    riwaya: 'hafs',
    downloadAllowed: true,
    licenseStatus: 'to_verify',
  },
  {
    id: 'shuraim',
    label: 'Saud Al-Shuraim',
    provider: DEFAULT_PROVIDER,
    bitrate: 128,
    edition: 'Saood_ash-Shuraym_128kbps',
    riwaya: 'hafs',
    downloadAllowed: true,
    licenseStatus: 'to_verify',
  },
  {
    id: 'muaiqly',
    label: 'Maher Al-Muaiqly',
    provider: DEFAULT_PROVIDER,
    bitrate: 128,
    edition: 'Maher_AlMuaiqly_128kbps',
    riwaya: 'hafs',
    downloadAllowed: true,
    licenseStatus: 'to_verify',
  },
  {
    id: 'minshawi',
    label: 'Mohammad Al-Minshawi (Murattal)',
    provider: DEFAULT_PROVIDER,
    bitrate: 128,
    edition: 'Minshawy_Murattal_128kbps',
    riwaya: 'hafs',
    downloadAllowed: true,
    licenseStatus: 'to_verify',
  },
  {
    id: 'husary',
    label: 'Mahmoud Khalil Al-Husary',
    provider: DEFAULT_PROVIDER,
    bitrate: 128,
    edition: 'Husary_128kbps',
    riwaya: 'hafs',
    downloadAllowed: true,
    licenseStatus: 'to_verify',
  },
];

const _pad3 = (n) => String(n).padStart(3, '0');

export const quranAudioProvider = {
  /** @returns {ReciterConfig[]} */
  getAvailableReciters() {
    return RECITERS.filter((r) => r.downloadAllowed);
  },

  /** @returns {ReciterConfig} */
  getReciterById(reciterId) {
    return RECITERS.find((r) => r.id === reciterId)
      || RECITERS.find((r) => r.id === DEFAULT_RECITER_ID);
  },

  /**
   * Build the remote audio URL for a given ayah.
   * @param {{ surahNumber: number, ayahNumber: number }} ayah
   */
  buildAyahRemoteUrl(ayah, reciterId) {
    const reciter = this.getReciterById(reciterId);
    if (!ayah || !ayah.surahNumber || !ayah.ayahNumber) return null;
    return `https://${reciter.provider}/data/${reciter.edition}/${_pad3(ayah.surahNumber)}${_pad3(ayah.ayahNumber)}.mp3`;
  },

  /** Build a fully shaped audio track for the player queue. */
  buildTrack(ayah, reciterId) {
    const reciter = this.getReciterById(reciterId);
    return {
      surahNumber: ayah.surahNumber,
      ayahNumber: ayah.ayahNumber,
      ayahKey: ayah.ayahKey,
      globalAyahNumber: ayah.globalAyahNumber,
      hizbNumber: ayah.hizbNumber,
      title: `Sourate ${ayah.surahNumber} — Verset ${ayah.ayahNumber}`,
      remoteUrl: this.buildAyahRemoteUrl(ayah, reciterId),
      reciterId: reciter.id,
      riwaya: reciter.riwaya,
    };
  },
};

export { DEFAULT_RECITER_ID };
